import type { EditorStore } from './editor-store';
import type { EditorCommand } from './editor-history';
import type {
  ObstacleDef, LevelCollectible, LevelSettings, LevelMeta,
  TerrainDef, LevelTrigger,
} from '@/game/level';

/** Deep-clone a JSON-serializable value. Undefined passes through unchanged
 *  (JSON.stringify(undefined) returns undefined, which JSON.parse can't handle). */
function clone<T>(v: T): T {
  return v === undefined ? v : JSON.parse(JSON.stringify(v));
}

/** Read a value at a dotted path. Returns undefined if any segment is missing. */
function readPath(obj: unknown, path: readonly (string | number)[]): unknown {
  let cur: unknown = obj;
  for (const seg of path) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[String(seg)];
  }
  return cur;
}

/** Write a value at a dotted path. Path segments must already exist. */
function writePath(obj: unknown, path: readonly (string | number)[], value: unknown): void {
  let cur = obj as Record<string, unknown>;
  for (let i = 0; i < path.length - 1; i++) {
    cur = cur[String(path[i])] as Record<string, unknown>;
  }
  cur[String(path[path.length - 1])] = value;
}

// ── Obstacles ────────────────────────────────────────────

export function addObstacle(store: EditorStore, segmentId: string, def: ObstacleDef): EditorCommand {
  const frozen = clone(def);
  let insertedAt = -1;
  return {
    kind: 'AddObstacle', targetKey: `${segmentId}`,
    do() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId)!;
        insertedAt = s.obstacles.length;
        s.obstacles.push(clone(frozen));
      });
    },
    undo() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId)!;
        s.obstacles.splice(insertedAt, 1);
      });
    },
  };
}

export function removeObstacle(store: EditorStore, segmentId: string, index: number): EditorCommand {
  let removed: ObstacleDef | null = null;
  return {
    kind: 'RemoveObstacle', targetKey: `${segmentId}:${index}`,
    do() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId)!;
        removed = clone(s.obstacles[index]);
        s.obstacles.splice(index, 1);
      });
    },
    undo() {
      if (!removed) return;
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId)!;
        s.obstacles.splice(index, 0, clone(removed as ObstacleDef));
      });
    },
  };
}

export function moveObstacle(
  store: EditorStore, segmentId: string, index: number,
  to: { along: number; across: number; up: number },
): EditorCommand {
  let before: { along: number; across: number; up: number } | null = null;
  let target = { ...to };
  const cmd: EditorCommand & { _nextTarget: typeof to } = {
    kind: 'MoveObstacle', targetKey: `${segmentId}:${index}`,
    _nextTarget: target,
    do() {
      store.mutate((lvl) => {
        const o = lvl.segments.find((x) => x.id === segmentId)!.obstacles[index];
        if (before === null) before = { ...o.position };
        o.position = { ...target };
      });
    },
    undo() {
      if (!before) return;
      store.mutate((lvl) => {
        const o = lvl.segments.find((x) => x.id === segmentId)!.obstacles[index];
        o.position = { ...before! };
      });
    },
    coalesceWith(next) {
      if (next.kind !== 'MoveObstacle' || next.targetKey !== this.targetKey) return false;
      const nx = next as typeof cmd;
      if (nx._nextTarget) { target = { ...nx._nextTarget }; cmd._nextTarget = target; return true; }
      return false;
    },
  };
  return cmd;
}

export function resizeObstacle(
  store: EditorStore, segmentId: string, index: number,
  to: { width: number; height: number },
): EditorCommand {
  let before: { width: number; height: number } | null = null;
  let target = { ...to };
  const cmd: EditorCommand & { _nextTarget: typeof to } = {
    kind: 'ResizeObstacle', targetKey: `${segmentId}:${index}`,
    _nextTarget: target,
    do() {
      store.mutate((lvl) => {
        const o = lvl.segments.find((x) => x.id === segmentId)!.obstacles[index];
        if (before === null) before = { ...o.size };
        o.size = { ...target };
      });
    },
    undo() {
      if (!before) return;
      store.mutate((lvl) => {
        const o = lvl.segments.find((x) => x.id === segmentId)!.obstacles[index];
        o.size = { ...before! };
      });
    },
    coalesceWith(next) {
      if (next.kind !== 'ResizeObstacle' || next.targetKey !== this.targetKey) return false;
      const nx = next as typeof cmd;
      if (nx._nextTarget) { target = { ...nx._nextTarget }; cmd._nextTarget = target; return true; }
      return false;
    },
  };
  return cmd;
}

export function updateObstacleField(
  store: EditorStore, segmentId: string, index: number,
  path: readonly (string | number)[], value: unknown,
): EditorCommand {
  let before: unknown = undefined;
  return {
    kind: 'UpdateObstacleField', targetKey: `${segmentId}:${index}:${path.join('.')}`,
    do() {
      store.mutate((lvl) => {
        const o = lvl.segments.find((x) => x.id === segmentId)!.obstacles[index];
        if (before === undefined) before = clone(readPath(o, path));
        writePath(o, path, clone(value));
      });
    },
    undo() {
      store.mutate((lvl) => {
        const o = lvl.segments.find((x) => x.id === segmentId)!.obstacles[index];
        writePath(o, path, before);
      });
    },
  };
}

// ── Collectibles ────────────────────────────────────────

export function addCollectible(store: EditorStore, def: LevelCollectible): EditorCommand {
  const frozen = clone(def);
  let at = -1;
  return {
    kind: 'AddCollectible', targetKey: 'collectibles',
    do() { store.mutate((lvl) => { at = lvl.collectibles.length; lvl.collectibles.push(clone(frozen)); }); },
    undo() { store.mutate((lvl) => { lvl.collectibles.splice(at, 1); }); },
  };
}

export function removeCollectible(store: EditorStore, index: number): EditorCommand {
  let removed: LevelCollectible | null = null;
  return {
    kind: 'RemoveCollectible', targetKey: `collectibles:${index}`,
    do() { store.mutate((lvl) => { removed = clone(lvl.collectibles[index]); lvl.collectibles.splice(index, 1); }); },
    undo() {
      if (!removed) return;
      store.mutate((lvl) => { lvl.collectibles.splice(index, 0, clone(removed as LevelCollectible)); });
    },
  };
}

// ── Settings / Meta / Terrain ─────────────────────────────

export function updateSettings(
  store: EditorStore, path: readonly (string | number)[], value: unknown,
): EditorCommand {
  let before: unknown = undefined;
  return {
    kind: 'UpdateSettings', targetKey: path.join('.'),
    do() {
      store.mutate((lvl) => {
        if (before === undefined) before = clone(readPath(lvl.settings as unknown, path));
        writePath(lvl.settings as unknown, path, clone(value));
      });
    },
    undo() { store.mutate((lvl) => { writePath(lvl.settings as unknown, path, before); }); },
  };
}

export function updateMeta(
  store: EditorStore, path: readonly (string | number)[], value: unknown,
): EditorCommand {
  let before: unknown = undefined;
  return {
    kind: 'UpdateMeta', targetKey: path.join('.'),
    do() {
      store.mutate((lvl) => {
        if (before === undefined) before = clone(readPath(lvl.meta as unknown, path));
        writePath(lvl.meta as unknown, path, clone(value));
      });
    },
    undo() { store.mutate((lvl) => { writePath(lvl.meta as unknown, path, before); }); },
  };
}

export function updateTerrain(store: EditorStore, segmentId: string, next: TerrainDef): EditorCommand {
  let before: TerrainDef | null = null;
  return {
    kind: 'UpdateTerrain', targetKey: segmentId,
    do() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId)!;
        if (before === null) before = clone(s.terrain);
        s.terrain = clone(next);
      });
    },
    undo() {
      if (!before) return;
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId)!;
        s.terrain = clone(before as TerrainDef);
      });
    },
  };
}

// ── Trigger stubs (full impl in Tier 2; signatures here so
// ── the command catalogue is complete and can be referenced by the toolbar.)

export function addTrigger(store: EditorStore, def: LevelTrigger): EditorCommand {
  const frozen = clone(def);
  let at = -1;
  return {
    kind: 'AddTrigger', targetKey: 'triggers',
    do() { store.mutate((lvl) => { at = lvl.triggers.length; lvl.triggers.push(clone(frozen)); }); },
    undo() { store.mutate((lvl) => { lvl.triggers.splice(at, 1); }); },
  };
}

export function removeTrigger(store: EditorStore, triggerId: string): EditorCommand {
  let removed: LevelTrigger | null = null;
  let at = -1;
  return {
    kind: 'RemoveTrigger', targetKey: triggerId,
    do() {
      store.mutate((lvl) => {
        at = lvl.triggers.findIndex((t) => t.id === triggerId);
        if (at < 0) return;
        removed = clone(lvl.triggers[at]);
        lvl.triggers.splice(at, 1);
      });
    },
    undo() {
      if (!removed) return;
      store.mutate((lvl) => { lvl.triggers.splice(at, 0, clone(removed as LevelTrigger)); });
    },
  };
}

export function updateTrigger(
  store: EditorStore, triggerId: string,
  path: readonly (string | number)[], value: unknown,
): EditorCommand {
  let before: unknown = undefined;
  return {
    kind: 'UpdateTrigger', targetKey: `${triggerId}:${path.join('.')}`,
    do() {
      store.mutate((lvl) => {
        const t = lvl.triggers.find((x) => x.id === triggerId);
        if (!t) return;
        if (before === undefined) before = clone(readPath(t as unknown, path));
        writePath(t as unknown, path, clone(value));
      });
    },
    undo() {
      store.mutate((lvl) => {
        const t = lvl.triggers.find((x) => x.id === triggerId);
        if (!t) return;
        writePath(t as unknown, path, before);
      });
    },
  };
}

// Suppress unused-import warnings for types only used in type positions
void (undefined as unknown as LevelSettings);
void (undefined as unknown as LevelMeta);

// ── Segments ─────────────────────────────────────────────

import type { LevelSegment } from '@/game/level';

export function addSegment(
  store: EditorStore, def: LevelSegment, index?: number,
): EditorCommand {
  const frozen = clone(def);
  let insertedAt = -1;
  return {
    kind: 'AddSegment', targetKey: 'segments',
    do() {
      store.mutate((lvl) => {
        insertedAt = index ?? lvl.segments.length;
        lvl.segments.splice(insertedAt, 0, clone(frozen));
      });
    },
    undo() {
      store.mutate((lvl) => { lvl.segments.splice(insertedAt, 1); });
    },
  };
}

export function removeSegment(store: EditorStore, segmentId: string): EditorCommand {
  let removed: LevelSegment | null = null;
  let at = -1;
  return {
    kind: 'RemoveSegment', targetKey: segmentId,
    do() {
      store.mutate((lvl) => {
        at = lvl.segments.findIndex((s) => s.id === segmentId);
        if (at < 0) return;
        removed = clone(lvl.segments[at]);
        lvl.segments.splice(at, 1);
      });
    },
    undo() {
      if (!removed) return;
      store.mutate((lvl) => { lvl.segments.splice(at, 0, clone(removed as LevelSegment)); });
    },
  };
}

export function duplicateObstacle(store: EditorStore, segmentId: string, index: number): EditorCommand {
  let insertedAt = -1;
  let snapshot: ObstacleDef | null = null;
  return {
    kind: 'DuplicateObstacle', targetKey: `${segmentId}:${index}`,
    do() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s || !s.obstacles[index]) return;
        if (snapshot === null) {
          snapshot = clone(s.obstacles[index]);
          snapshot.position = { ...snapshot.position, along: snapshot.position.along + 40 };
        }
        insertedAt = index + 1;
        s.obstacles.splice(insertedAt, 0, clone(snapshot));
      });
    },
    undo() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s) return;
        s.obstacles.splice(insertedAt, 1);
      });
    },
  };
}

export function reorderSegments(store: EditorStore, newOrder: string[]): EditorCommand {
  let before: string[] = [];
  return {
    kind: 'ReorderSegments', targetKey: 'segments',
    do() {
      store.mutate((lvl) => {
        if (before.length === 0) before = lvl.segments.map((s) => s.id);
        const byId = new Map(lvl.segments.map((s) => [s.id, s]));
        lvl.segments = newOrder.map((id) => byId.get(id)!).filter(Boolean);
      });
    },
    undo() {
      store.mutate((lvl) => {
        const byId = new Map(lvl.segments.map((s) => [s.id, s]));
        lvl.segments = before.map((id) => byId.get(id)!).filter(Boolean);
      });
    },
  };
}

// ── Decorations ──────────────────────────────────────────

import type { DecorationDef } from '@/game/level';

export function addDecoration(store: EditorStore, segmentId: string, def: DecorationDef): EditorCommand {
  const frozen = clone(def);
  let at = -1;
  return {
    kind: 'AddDecoration', targetKey: segmentId,
    do() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s) return;
        at = s.decorations.length;
        s.decorations.push(clone(frozen));
      });
    },
    undo() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s) return;
        s.decorations.splice(at, 1);
      });
    },
  };
}

export function removeDecoration(store: EditorStore, segmentId: string, index: number): EditorCommand {
  let removed: DecorationDef | null = null;
  return {
    kind: 'RemoveDecoration', targetKey: `${segmentId}:${index}`,
    do() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s) return;
        removed = clone(s.decorations[index]);
        s.decorations.splice(index, 1);
      });
    },
    undo() {
      if (!removed) return;
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s) return;
        s.decorations.splice(index, 0, clone(removed as DecorationDef));
      });
    },
  };
}

export function updateDecorationField(
  store: EditorStore, segmentId: string, index: number,
  path: readonly (string | number)[], value: unknown,
): EditorCommand {
  let before: unknown = undefined;
  return {
    kind: 'UpdateDecorationField', targetKey: `${segmentId}:${index}:${path.join('.')}`,
    do() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s) return;
        const d = s.decorations[index];
        if (!d) return;
        if (before === undefined) before = clone(readPath(d as unknown, path));
        writePath(d as unknown, path, clone(value));
      });
    },
    undo() {
      store.mutate((lvl) => {
        const s = lvl.segments.find((x) => x.id === segmentId);
        if (!s) return;
        writePath(s.decorations[index] as unknown, path, before);
      });
    },
  };
}

export function duplicateSegment(
  store: EditorStore, segmentId: string, mirror = false,
): EditorCommand {
  let insertedAt = -1;
  let newId = '';
  return {
    kind: 'DuplicateSegment', targetKey: segmentId,
    do() {
      store.mutate((lvl) => {
        const idx = lvl.segments.findIndex((s) => s.id === segmentId);
        if (idx < 0) return;
        const copy = clone(lvl.segments[idx]);
        if (newId === '') newId = `seg-${crypto.randomUUID().slice(0, 6)}`;
        copy.id = newId;
        if (mirror) {
          const len = copy.length;
          for (const o of copy.obstacles) {
            o.position.along = len - o.position.along;
          }
        }
        insertedAt = idx + 1;
        lvl.segments.splice(insertedAt, 0, copy);
      });
    },
    undo() {
      store.mutate((lvl) => { lvl.segments.splice(insertedAt, 1); });
    },
  };
}
